import {useNavigate, useParams} from "react-router-dom";
import {useEffect, useMemo, useState} from "react";
import ClientForm from "./ClientForm";
import {useNotification} from "../../hooks/useNotification";
import {useFormik} from "formik";
import * as yup from "yup";
import baseFormStyles from "../../components/BaseForm.module.scss";
import Button from "../../components/Button/Button";
import Icon, {plusIcon, companyIcon} from "../../components/Icon/Icon";
import PageLayout from "../../components/PageLayout/PageLayout";
import Avatar from "../../components/Avatar/Avatar";
import {capitalize} from "../../utils/Utils";
import Actions from "../../components/Actions/Actions";
import Table from "../../components/Table/Table";
import UserModal from "../User/UserModal";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import UserActions from "../../api/UsersActions";
import ClientActions from "../../api/ClientsActions";

const validationSchema = yup.object({
    name: yup.string().required("Company name is required"),
    email: yup.string()
        .email("Email data is incorrect.")
        .required("Email is required."),
    color: yup.string().required("Color is required"),
});

const Client = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const [currentTab, setCurrentTab] = useState("details");
    const [modal, setModal] = useState(null);
    const [deleteModal, setDeleteModal] = useState(null);


    const {triggerNotification} = useNotification();

    const formik = useFormik({
        initialValues: {
            id: null,
            name: "",
            email: "",
            color: null,
            logo: null,
            users: [],
        },
        validationSchema,
        onSubmit: (values) => {
            ClientActions.save(values, res => {
                if(values.id){
                    triggerNotification("Client updated successfully");
                    formik.setValues(res)
                }else{
                    navigate("/clients")
                    triggerNotification("Client created successfully");
                    formik.setValues(res)
                }
            })
        },
    });

    useEffect(() => {
        if (!id) {
            return;
        }

        ClientActions.getClient(id, (response) => {
            formik.setValues({
                id: response.id ?? null,
                name: response.name ?? "",
                email: response.email ?? "",
                color: response.color ?? null,
                logo: response.logo ?? null,
                users: response.users ?? [],
                prevName: response.name ?? "",
            });
        });
    }, [id]);

    const columns = useMemo(() => [
        {
            accessorKey: 'name',
            header: 'Name',
            cell: ({row}) => {
                const {name, surname, color, image} = row.original;

                return (
                    <div>
                        <Avatar
                            name={name}
                            surname={surname}
                            url={image?.url}
                            color={color}
                        />
                        {name} {surname}
                    </div>
                )
            }
        },
        {
            accessorKey: 'email',
            header: 'Email',
        },
        {
            accessorKey: 'role',
            header: 'Role',
            cell: ({row}) => {
                const role = row.original.role;
                return (
                    <p className="role">{capitalize(role)}</p>
                )
            }
        },
        {
            accessorKey: 'actions',
            header: 'Actions',
            cell: ({row}) => {
                const role = row.original.role;
                return (
                    <Actions
                        onEdit={() => setModal(row.original)}
                        {...(role !== 'superadmin' ? { onDelete: () => setDeleteModal(row.original) } : {})}
                    />
                );
            }
        },
    ], [formik.values.users]);


    return (
        <PageLayout
            titlePage="Clients"
            titlePageContent={formik.values.prevName ?? ""}
            icon={companyIcon}
            headerTabs={
                [
                    {
                        label: "Company Info",
                        tabId: "details",
                        defaultTab: true,
                    },
                    {
                        label: "Users",
                        tabId: "users",
                        disabled: !formik.values.id,
                    }
                ]
            }
            currentTab={currentTab}
            onTabChange={setCurrentTab}
            className={baseFormStyles["base-form"]}
            actions={
                currentTab === "users" ? (
                    <Button
                        className="add"
                        onClick={() => setModal({})}
                    >
                        <Icon svg={plusIcon} />
                        Add New
                    </Button>
                ) : (
                    <>
                        <Button type="submit" form="client-form">
                            Save
                        </Button>
                        <Button
                            to="/clients"
                            variant="secondary"
                            className="cancel"
                        >
                            Cancel
                        </Button>
                    </>
                )
            }
        >
            {currentTab === "details" && <ClientForm formik={formik}/>}

            {currentTab === "users" && (
                <>
                    <Table data={formik.values.users} columns={columns}/>
                    {
                        modal &&
                        <UserModal
                            onClose={() => setModal(null)}
                            title={modal?.id ? 'Edit user' : 'Add user'}
                            user={modal}
                            onSubmit={(values) => {
                                if(!values.id){
                                    UserActions.create({...values, client: {id, name: formik.values.name}}, res => {
                                        formik.setFieldValue('users', [...formik.values.users, res]);
                                        setModal(null);
                                        triggerNotification('User added successfully');
                                    })
                                }else{
                                    UserActions.updateUser(values.id, values, res => {
                                        formik.setFieldValue('users', formik.values.users.map(user => user.id === values.id ? res : user));
                                        setModal(null);
                                        triggerNotification('User updated successfully');
                                    })
                                }
                            }}
                        />
                    }
                    {
                        deleteModal &&
                        <DeleteModal
                            title="Delete user"
                            onClose={() => setDeleteModal(null)}
                            message="Are you sure you want to delete this user?"
                            onConfirm={() => UserActions.deleteUser(deleteModal.id, _ => {
                                formik.setFieldValue('users', formik.values.users.filter(user => user.id !== deleteModal.id));
                                setDeleteModal(null);
                                triggerNotification('User deleted successfully');
                            })}
                        />
                    }
                </>
            )}
        </PageLayout>
    );
};

export default Client;
